import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard, Building2, Users, FileText, Receipt, Calculator,
  FolderLock, ShieldAlert, LogOut, Sun, Moon, Menu, Compass, Scale, LineChart, Settings,
  Brain, UserCog, Target, Sparkles, Wallet, Folder, TrendingUp, Bot, Files, ChevronDown,
  Gauge, Landmark,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useI18n } from "@/context/I18nContext";
import { LANGUAGES } from "@/i18n/translations";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Chatbot from "@/components/Chatbot";
import NotificationBell from "@/components/NotificationBell";
import InstallAppButton from "@/components/InstallAppButton";
import MobileBottomNav from "@/components/MobileBottomNav";

const NAV_GROUPS_STORAGE_KEY = "yp_nav_groups";
const DEFAULT_OPEN_GROUPS = { management: true };

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { lang, setLang, t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(NAV_GROUPS_STORAGE_KEY) || "null");
      if (saved) return saved;
    } catch { /* ignore malformed storage */ }
    return DEFAULT_OPEN_GROUPS;
  });

  const navGroups = user?.role === "accountant"
    ? [
        { items: [{ to: "/accountant/clients", icon: Users, label: t("nav_accountant_clients") }] },
        { items: [{ to: "/settings", icon: Settings, label: t("nav_settings") }] },
      ]
    : [
        { items: [{ to: "/dashboard", icon: LayoutDashboard, label: t("nav_dashboard") }] },
        {
          key: "management", icon: Folder, label: t("nav_group_management"),
          items: [
            { to: "/properties", icon: Building2, label: t("nav_properties") },
            { to: "/tenants", icon: Users, label: t("nav_tenants") },
            { to: "/contracts", icon: FileText, label: t("nav_contracts") },
            { to: "/payments", icon: Wallet, label: t("nav_payments") },
            { to: "/expenses", icon: Receipt, label: t("nav_expenses") },
          ],
        },
        {
          key: "analysis", icon: TrendingUp, label: t("nav_group_analysis"),
          items: [
            { to: "/simulator", icon: Calculator, label: t("nav_simulator") },
            { to: "/deal-analyzer", icon: Target, label: t("nav_deal_analyzer") },
            { to: "/portfolio-optimizer", icon: Sparkles, label: t("nav_portfolio_optimizer") },
            { to: "/market", icon: LineChart, label: t("nav_market") },
          ],
        },
        {
          key: "ai", icon: Bot, label: t("nav_group_ai"),
          items: [
            { to: "/advisor", icon: Gauge, label: t("nav_advisor") },
            { to: "/legal", icon: Scale, label: t("nav_legal") },
            { to: "/finance-ai", icon: Brain, label: t("nav_finance_ai") },
          ],
        },
        {
          key: "documents", icon: Files, label: t("nav_group_documents"),
          items: [
            { to: "/documents", icon: FolderLock, label: t("nav_documents") },
            { to: "/accountant", icon: UserCog, label: t("nav_accountant") },
            { to: "/tax-report", icon: Landmark, label: t("nav_tax_assistant") },
          ],
        },
        // Marketplace intentionally not linked in the nav yet — it's a real
        // route/backend, just held back for phase 2 (post-launch, once there
        // are actual users/partners to show). Re-add the entry here to bring
        // it back.
        { items: [{ to: "/settings", icon: Settings, label: t("nav_settings") }] },
      ];

  // Accordion behavior: only one group open at a time. `key = null` collapses
  // everything (used when the currently-open group is clicked again).
  const withOnlyGroupOpen = (key) => {
    const next = {};
    for (const g of navGroups) if (g.key) next[g.key] = g.key === key;
    return next;
  };

  // Keep whichever group contains the current route expanded — e.g. navigating
  // straight to /finance-ai (bookmark, deep link) opens "IA & Automação" even if
  // it was collapsed before, closing whatever else was open.
  useEffect(() => {
    const activeGroup = navGroups.find((g) => g.key && g.items.some((n) => location.pathname.startsWith(n.to)));
    if (activeGroup && !openGroups[activeGroup.key]) {
      setOpenGroups(withOnlyGroupOpen(activeGroup.key));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    localStorage.setItem(NAV_GROUPS_STORAGE_KEY, JSON.stringify(openGroups));
  }, [openGroups]);

  const bottomNavItems = user?.role === "accountant"
    ? [
        { to: "/accountant/clients", icon: Users, label: t("nav_accountant_clients") },
        { to: "/settings", icon: Settings, label: t("nav_settings") },
      ]
    : [
        { to: "/dashboard", icon: LayoutDashboard, label: t("nav_dashboard") },
        { to: "/properties", icon: Building2, label: t("nav_properties") },
        { to: "/payments", icon: Wallet, label: t("nav_payments") },
        { to: "/advisor", icon: Gauge, label: t("nav_advisor") },
      ];

  const currentLang = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  const navLinkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
      isActive
        ? "bg-secondary text-foreground font-medium"
        : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
    }`;

  const SidebarContent = () => (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 px-6 h-16 border-b border-border">
        <Compass className="h-5 w-5 text-[hsl(var(--success))]" strokeWidth={2.2} />
        <div className="leading-none">
          <span className="font-display text-xl tracking-tight">YieldPilot</span>
          <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-0.5">
            {t("brand_tag")}
          </p>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navGroups.map((group, gi) => {
          if (!group.key) {
            // Ungrouped, always-visible item (Painel, Marketplace, Definições).
            const n = group.items[0];
            return (
              <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}
                data-testid={`nav-${n.to.slice(1)}`} className={navLinkClass}>
                <n.icon className="h-4 w-4" />
                {n.label}
              </NavLink>
            );
          }
          const isOpen = !!openGroups[group.key];
          return (
            <div key={group.key}>
              <button
                type="button"
                onClick={() => setOpenGroups((prev) => withOnlyGroupOpen(prev[group.key] ? null : group.key))}
                data-testid={`nav-group-${group.key}`}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
              >
                <group.icon className="h-4 w-4" />
                <span className="flex-1 text-left">{group.label}</span>
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="pl-4 space-y-1 mt-1">
                  {group.items.map((n) => (
                    <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}
                      data-testid={`nav-${n.to.slice(1)}`} className={navLinkClass}>
                      <n.icon className="h-4 w-4" />
                      {n.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
      {user?.role === "admin" && (
        <div className="px-3 pt-2">
          <NavLink
            to="/admin"
            data-testid="nav-super-admin"
            className="flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm text-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))]/10 transition-colors"
          >
            <ShieldAlert className="h-4 w-4" />
            {t("nav_admin")}
          </NavLink>
        </div>
      )}
      <div className="px-3 py-4 border-t border-border">
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center font-mono text-sm">
            {(user?.name || "U").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          data-testid="logout-btn"
          onClick={() => {
            logout();
            navigate("/login");
          }}
          className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground mt-1"
        >
          <LogOut className="h-4 w-4" />
          {t("logout")}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-border fixed inset-y-0">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar */}
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64 bg-background border-r border-border">
            <SidebarContent />
          </div>
        </div>
      )}

      <div className="flex-1 lg:pl-64 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 h-16 border-b border-border backdrop-blur-xl bg-background/70 flex items-center justify-between px-4 lg:px-8">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <InstallAppButton />
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="lang-switcher" className="gap-2 rounded-sm">
                  <span>{currentLang.flag}</span>
                  <span className="hidden sm:inline text-xs uppercase">{currentLang.code}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-popover">
                {LANGUAGES.map((l) => (
                  <DropdownMenuItem
                    key={l.code}
                    data-testid={`lang-${l.code}`}
                    onClick={() => setLang(l.code)}
                    className="gap-2 cursor-pointer"
                  >
                    <span>{l.flag}</span> {l.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              size="icon"
              onClick={toggle}
              data-testid="theme-toggle"
              className="rounded-sm"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
          </div>
        </header>

        <motion.main
          key={window.location.pathname}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex-1 p-4 pb-24 lg:p-8 max-w-[1400px] w-full"
        >
          <Outlet />
        </motion.main>
      </div>

      <MobileBottomNav items={bottomNavItems} onMore={() => setOpen(true)} />
      <Chatbot />
    </div>
  );
}
